import { Filter, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useIntl } from '@/core/i18n/use-intl';
import { Badge, Button } from '@/shared/components/ui';
import { cn } from '@/shared/utils';

interface TableFiltersBarProps {
  children: ReactNode;
  className?: string;
  label?: ReactNode;
  clearAll?: ReactNode;
}

export function TableFiltersBar({ children, className, label, clearAll }: TableFiltersBarProps) {
  const { t } = useIntl();

  return (
    <div className={cn('w-full flex flex-col gap-2', className)}>
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider pr-2 border-r border-border">
            <Filter className="size-4" aria-hidden />
            <span>{label ?? t('common.filter')}</span>
          </div>
          {children}
        </div>
        {clearAll ? <div className="shrink-0 self-start">{clearAll}</div> : null}
      </div>
    </div>
  );
}

export type TableActiveFilterTag = {
  id: string;
  label: ReactNode;
  onRemove: () => void;
  testId?: string;
};

interface TableActiveFilterTagsProps {
  tags: TableActiveFilterTag[];
  className?: string;
}

export function TableActiveFilterTags({ tags, className }: TableActiveFilterTagsProps) {
  const { t } = useIntl();
  if (tags.length === 0) return null;

  return (
    <div className={cn('flex flex-wrap gap-2', className)}>
      {tags.map((tag) => (
        <Badge
          key={tag.id}
          variant="secondary"
          className="pl-2 pr-1 py-1 gap-1 text-muted-foreground bg-muted border border-border"
          data-testid={tag.testId}
        >
          <span className="flex items-center gap-1 min-w-0">{tag.label}</span>
          <button
            type="button"
            className="ml-1 rounded-sm hover:bg-muted-foreground/10 p-0.5 shrink-0"
            onClick={tag.onRemove}
            aria-label={t('common.remove')}
          >
            <X size={14} aria-hidden />
          </button>
        </Badge>
      ))}
    </div>
  );
}

interface TableClearAllButtonProps {
  onClick: () => void;
  className?: string;
  testId?: string;
}

export function TableClearAllButton({ onClick, className, testId }: TableClearAllButtonProps) {
  const { t } = useIntl();
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={onClick}
      data-testid={testId}
      className={cn('text-muted-foreground hover:text-destructive', className)}
    >
      <X className="size-4" aria-hidden />
      {t('common.clearAll')}
    </Button>
  );
}
